import InfoCard from '@/components/ui/InfoCard';
import { Button } from '@/components/ui/button';
import { CheckCircle2, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatMoney } from '@/lib/formatters';
import { Tabs, TabsContent, TabsList } from '@/components/ui/tabs';
import { UpcomingPaymentGroup, UpcomingPaymentItem, UpcomingPaymentsProps } from './types';

const UpcomingPayments = (props: UpcomingPaymentsProps) => {
  const {
    allCovered,
    variant
  } = props;

  if (variant === 'summary') {
    const {
      groups,
      onCreateShipment
    } = props;

    if (allCovered || groups.length === 0) {
      return (
        <InfoCard
          action={(
            <Button
              className="w-full"
              onClick={onCreateShipment}
              size="lg"
              type="button"
            >
              Создать поставку
            </Button>
          )}
          description={(
            <>
              Нет активных поставок. Когда вы согласуете коммерческое предложение, здесь появится информация по
              оплатам.
            </>
          )}
          icon={(
            <CheckCircle2
              className="size-6 text-muted-foreground-soft"
              strokeWidth={1.4}
            />
          )}
        />
      );
    }

    return (
      <div className="flex flex-col overflow-hidden px-4 py-2 rounded-2xl border border-border-strong bg-surface">
        {groups.map((group) => (
          <div
            className={cn(
              'relative flex flex-col gap-2 py-2 border-b border-border-strong last:border-b-0',
              group.hasRedUrgency && 'pl-3'
            )}
            key={group.id}
          >
            {group.hasRedUrgency ? (
              <div className="absolute bottom-2 left-0 top-2 w-px bg-destructive" />
            ) : null}
            <div className="flex items-center justify-between gap-4">
              <p className="min-w-0 truncate font-sans text-sm font-semibold leading-5 text-foreground">
                {group.shipmentNumber}
              </p>
              <p className="shrink-0 font-sans text-sm font-semibold leading-5 text-foreground">
                {formatMoney({ currency: 'RUB', value: group.totalAmount })}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              {group.items.map((item) => (
                <p
                  className={cn(
                    'font-sans text-xs leading-4',
                    item.urgency === 'RED' ? 'text-destructive' : 'text-muted-foreground'
                  )}
                  key={item.id}
                >
                  {item.title ?? 'Платёж'} · {formatMoney({ currency: 'RUB', value: item.amount })} · {formatDate({
                    value: item.date,
                    variant: 'date'
                  })}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const {
    activeTab,
    groups,
    onPayPayment,
    onSwitchTab,
    summary,
    tabCounts,
    tabs
  } = props;

  const renderPaymentItem = (item: UpcomingPaymentItem) => {
    return (
      <p
        className={cn(
          'font-sans text-xs leading-4',
          item.urgency === 'RED' ? 'text-destructive' : 'text-muted-foreground'
        )}
        key={item.id}
      >
        {item.title ?? 'Платёж'} · {formatMoney({ currency: 'RUB', value: item.amount })} · {formatDate({
          value: item.date,
          variant: 'date'
        })}
      </p>
    );
  };

  const onClickPayPaymentGroup = (group: UpcomingPaymentGroup): void => {
    onPayPayment(group);
  };

  const renderPaymentGroupCard = (group: UpcomingPaymentGroup) => {
    return (
      <div
        className="relative flex flex-col gap-3 overflow-hidden px-4 py-3 rounded-2xl border border-border-strong bg-surface"
        key={group.id}
      >
        {group.hasRedUrgency ? (
          <div className="absolute bottom-0 left-0 top-0 w-[3px] bg-destructive" />
        ) : null}
        <div className="flex items-center justify-between gap-4">
          <p className="min-w-0 truncate font-sans text-sm font-semibold leading-5 text-foreground">
            {group.shipmentNumber}
          </p>
          <p className="shrink-0 font-sans text-sm font-semibold leading-5 text-foreground">
            {formatMoney({ currency: 'RUB', value: group.totalAmount })}
          </p>
        </div>
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {group.items.map(renderPaymentItem)}
          </div>
          <Button
            className="shrink-0"
            onClick={() => onClickPayPaymentGroup(group)}
            size="md"
            type="button"
          >
            Оплатить
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Tabs
      className="flex flex-col gap-6"
      value={activeTab}
    >
      <TabsList className="flex h-auto w-full justify-start gap-4 overflow-x-auto p-0 text-foreground bg-transparent rounded-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {tabs.map((tab) => (
          <button
            aria-selected={activeTab === tab.value}
            className={cn(
              'relative shrink-0 px-1 py-2',
              'font-sans text-sm font-semibold leading-5 whitespace-nowrap text-foreground',
              'border-b border-transparent',
              'transition-colors',
              activeTab === tab.value && 'border-[hsl(var(--border-focus))]'
            )}
            data-state={activeTab === tab.value ? 'active' : 'inactive'}
            key={tab.value}
            onClick={() => onSwitchTab(tab.value)}
            role="tab"
            tabIndex={activeTab === tab.value ? 0 : -1}
            type="button"
          >
            {tab.label} ({tabCounts[tab.value] ?? 0})
          </button>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent
          className="mt-0"
          key={tab.value}
          value={tab.value}
        >
          {activeTab === tab.value && (
            <div className="flex flex-col gap-2 opacity-80">
              <div className="flex items-center gap-2">
                <TrendingUp
                  className="size-4 shrink-0 text-muted-foreground"
                  strokeWidth={1.8}
                />
                <p className="font-sans text-xs leading-4 text-muted-foreground">{summary}</p>
              </div>
              <div className="flex flex-col gap-2">
                {groups.map(renderPaymentGroupCard)}
              </div>
            </div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default UpcomingPayments;